 <template>
  <div class="py-6 px-8">
    <h-header :back="hostName ? '../payments' : ''">
      <template v-slot:actions>
        <v-btn color="primary" elevation="0" @click="generateLinkExcelAppointments()">
          <v-icon left>mdi-arrow-down-bold-box-outline</v-icon>
          <span class="d-none d-md-flex body-2">
            {{ $t('appointment.buttons.generateExcel') }}
          </span>
        </v-btn>
      </template>
    </h-header>

    <h3 v-if="hostName" class="mb-5 ml-11 mt-n5 grey--text display-1">
      {{ hostName }}
    </h3>

    <AdminAppointmentListFilters ref="appointmentListFilters" />

    <AppointmentListShowExtraContent
      @setShowExtraHeaders="setShowExtraHeaders"
      :selected-appointments="selectedAppointments"
    />

    <h-draggable>
      <v-data-table
        @dblclick:row="showAppointmentRow"
        v-model="selectedAppointments"
        :items-per-page="itemsPerPage"
        :items="appointments"
        :loading="isLoading"
        :headers="headers"
        :no-data-text="$t('appointment.table.noData')"
        :loading-text="$t('appointment.table.loading')"
        mobile-breakpoint="20"
        class="clickable"
        hide-default-footer
        disable-sort
        show-select
      >
        <template v-slot:[`item.code`]="{ item }">
          <span class="font-weight-bold">
            {{ item.code }}
          </span>
        </template>
        <template v-slot:[`item.type`]="{ item }">
          <span class="d-block text-truncate mw-200">
            {{ $t(`type.${item.type}`) }}
          </span>
        </template>
        <template v-slot:[`item.realtiesCode`]="{ item }">
          <AppointmentListRealties
            :show-extra-content="showExtraHeaders"
            :realties="item.realties"
          />
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :class="formatColors(item.status)" small>
            {{ displayContent($t(`status.${item.status}`)) }}
          </v-chip>
        </template>
        <template v-slot:[`item.date`]="{ item }">
          {{ displayContent(timeSlot(item.date, item.slot_size)) }}
        </template>
        <template v-slot:[`item.cancel_date`]="{ item }">
          {{ displayContent(item.cancel_date, 'date') }}
        </template>
        <template v-slot:[`item.group`]="{ item }">
          <span :class="item.group ? 'font-weight-bold' : ''">
            {{ displayContent(item.group && item.group.code) }}
          </span>
        </template>
        <template v-slot:[`item.worker_type`]="{ item }">
          {{ displayContent(item.worker_type, 'workerType') }}
        </template>
        <template v-slot:[`item.host`]="{ item }">
          {{ displayContent(item.host && item.host.name) }}
        </template>
        <template v-slot:[`item.real_estate`]="{ item }">
          {{ displayContent(item.real_estate && item.real_estate.name) }}
        </template>
        <template v-slot:[`item.branch`]="{ item }">
          {{ displayContent(item.branch && item.branch.name) }}
        </template>
        <template v-slot:[`item.observations`]="{ item }">
          {{ displayContent(item.observations) }}
        </template>
        <template v-slot:[`item.visitor_name`]="{ item }">
          {{ displayContent(item.visitor_name) }}
        </template>
        <template v-slot:[`item.visitor_phone`]="{ item }">
          {{ displayContent(item.visitor_phone, 'phone') }}
        </template>
        <template v-slot:[`item.additional_data`]="{ item }">
          <div v-if="item.additional_data" v-for="additionalData in item.additional_data">
            <span class="font-weight-bold caption">
              {{ additionalData.title }}:
            </span>
            <span>
              {{ additionalData.value }}
            </span>
          </div>
          <div v-if="item.cancel_reason">
            <span class="font-weight-bold caption">
              {{ $t('appointment.label.cancelReason') }}:
            </span>
            <span>
              {{ item.cancel_reason }}
            </span>
          </div>
        </template>
        <template v-slot:item.confirmation="{ item }">
          <ShowConfirmedStatus
            :confirmation="item.confirmation"
          />
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-end">
            <v-menu bottom rounded offset-y>
              <template v-slot:activator="{ on }">
                <v-btn color="grey" elevation="0" v-on="on" icon>
                  <v-icon small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-card>
                <v-list dense>
                  <span v-for="(option, index) in options(item, item.group ? item.group.id : undefined, true)" :key="index">
                    <v-list-item
                      @click="action(item, option)"
                      v-if="option.show"
                      link
                    >
                      <v-icon class="mr-3 grey--text text--lighten-1" style="transform: scale(0.85); opacity: 0.75">
                        {{ option.icon }}
                      </v-icon>
                      <span class="body-2">
                        {{ option.title }}
                      </span>
                    </v-list-item>
                  </span>
                </v-list>
              </v-card>
            </v-menu>
          </div>
        </template>
      </v-data-table>
    </h-draggable>

    <h-pagination :fetch="fetchAppointmentsAsAdmin" />

    <ModalAppointmentVisitorEdit isAdmin />
    <ModalUpdateAdditionalInfo v-if="appointment" @refreshMap="refreshAppointments" :additional-data="additionalData" />
    <ModalUpdateAdditionalInfoConfirmToClose />

    <AdminAppointmentChangeKeyLocation />
    <AdminAppointmentChangeStatus />
    <AdminAppointmentQuoteReopen />
    <AdminAppointmentChangeDate />
    <AdminAppointmentAddHost />
    <AdminAppointmentChangeHost />
    <AdminAppointmentChangeType />
    <AppointmentReschedule isAdmin />
    <AppointmentCancel isAdmin />
    <AppointmentUpdateObservations />
    <AppointmentUpdateDisplacement />

    <ModalConfirmAppointmentManually @refreshMap="refreshAppointments" :appointment="appointment" />
    <ModalBulkReschedule @refreshMap="refreshAppointments" :selectedAppointments="selectedAppointments" />
    <ModalBulkCancel @refreshMap="refreshAppointments" :selectedAppointments="selectedAppointments" />
    <ModalBulkChangeAppointmentStatus @refreshMap="refreshAppointments" :selectedAppointments="selectedAppointments" />
    <ModalBulkSendConfirmationEmail @refreshMap="refreshAppointments" :selectedAppointments="appointmentsToConfirmByEmail" />
    <ModalBulkSendRejectedEmail @refreshMap="refreshAppointments" :selectedAppointments="appointmentsToConfirmByEmail" />
  </div>
</template>

<script>
import AdminAppointmentListFilters from '@/components/appointment/AdminAppointmentListFilters'
import AppointmentListRealties from '@/components/appointment/fragments/AppointmentListRealties'

import HDraggable from '@/components/app/HDraggable'

import AppointmentCancel from '@/components/appointment/AppointmentCancel'
import AppointmentReschedule from '@/components/appointment/AppointmentReschedule'

import AdminAppointmentAddHost from '@/components/appointment/AdminAppointmentAddHost'
import AdminAppointmentChangeType from '@/components/appointment/AdminAppointmentChangeType'
import AdminAppointmentChangeHost from '@/components/appointment/AdminAppointmentChangeHost'
import AdminAppointmentChangeDate from '@/components/appointment/AdminAppointmentChangeDate'
import AdminAppointmentQuoteReopen from '@/components/appointment/AdminAppointmentQuoteReopen'
import AdminAppointmentChangeStatus from '@/components/appointment/AdminAppointmentChangeStatus'
import AdminAppointmentChangeKeyLocation from '@/components/appointment/AdminAppointmentChangeKeyLocation'

import ModalUpdateAdditionalInfo from '@/components/appointment/modals/ModalUpdateAdditionalInfo'
import ModalUpdateAdditionalInfoConfirmToClose from '@/components/appointment/modals/ModalUpdateAdditionalInfoConfirmToClose'

import ModalBulkCancel from '@/components/appointment/modals/ModalBulkCancel'
import ModalBulkReschedule from '@/components/appointment/modals/ModalBulkReschedule'
import ModalAppointmentVisitorEdit from '@/components/appointment/modals/ModalAppointmentVisitorEdit'
import ModalBulkSendConfirmationEmail from '@/components/appointment/modals/ModalBulkSendConfirmationEmail'
import ModalBulkSendRejectedEmail from '@/components/appointment/modals/ModalBulkSendRejectedEmail'
import ModalConfirmAppointmentManually from '@/components/appointment/modals/ModalConfirmAppointmentManually'
import ModalBulkChangeAppointmentStatus from '@/components/appointment/modals/ModalBulkChangeAppointmentStatus'
import AppointmentUpdateObservations from '@/components/appointment/AppointmentUpdateObservations'
import AppointmentUpdateDisplacement from '@/components/appointment/AppointmentUpdateDisplacement'

import AppointmentListShowExtraContent from '@/components/appointment/details/fragments/AppointmentListShowExtraContent'

import { mapGetters, mapActions, mapMutations } from 'vuex'
import { advancedAppointmentHeaders, appointmentHeaders } from './listHeaders/appointmentHeaders'
import { handleColors } from '@/util/colors'
import { timeSlot, displayContent } from '@/util/catalog'

import { options } from '../components/appointment/menuAdminAppointment'
import ShowConfirmedStatus from './mapDrawing/fragments/ShowConfirmedStatus.vue'

export default {
  name: 'AdminAppointmentList',

  components: {
    HDraggable,
    AdminAppointmentListFilters,
    AppointmentListRealties,

    ModalBulkCancel,
    ModalBulkReschedule,
    ModalAppointmentVisitorEdit,
    ModalBulkSendConfirmationEmail,
    ModalBulkSendRejectedEmail,

    ModalUpdateAdditionalInfo,
    ModalUpdateAdditionalInfoConfirmToClose,

    AppointmentCancel,
    AppointmentReschedule,
    AppointmentUpdateObservations,
    AppointmentUpdateDisplacement,

    AdminAppointmentAddHost,
    AdminAppointmentChangeType,
    AdminAppointmentChangeHost,
    AdminAppointmentChangeDate,
    AdminAppointmentQuoteReopen,
    AdminAppointmentChangeStatus,
    AdminAppointmentChangeKeyLocation,
    ModalConfirmAppointmentManually,
    ModalBulkChangeAppointmentStatus,

    AppointmentListShowExtraContent,

    ShowConfirmedStatus
  },

  data() {
    return {
      timeSlot,
      displayContent,

      hostId: '',
      hostName: '',
      formatColors: handleColors,
      showExtraHeaders: false,
      selectedAppointments: [],

      options: options,
      menuOptions: [],
    }
  },

  computed: {
    ...mapGetters(['isLoading', 'appointments', 'itemsPerPage', 'appointment']),

    headers() {
      return this.showExtraHeaders ? advancedAppointmentHeaders : appointmentHeaders
    },

    additionalData() {
      return this.appointment?.additional_data ?? []
    },

    appointmentsToConfirmByEmail() {
      let appointments = []

      if (this.selectedAppointments.length > 0) {
        appointments = this.selectedAppointments
      } else {
        appointments = [this.appointment]
      }

      return appointments
    }
  },

  methods: {
    ...mapActions([
      'fetchAppointmentsAsAdmin',
      'fetchAppointmentAsAdmin',
      'showErrorMessage',
      'generateAdminAppointmentExcel',
      'openModal',
    ]),

    ...mapMutations([
      'setFilters',
      'setIsLoading',
      'setAppointments',
      'setIsAppointmentLoading',
    ]),

    showAppointmentRow(_, { item }) {
      window.open(`/admin-appointment/${item?.id}`)
    },

    showAppointment(id) {
      window.open(`/admin-appointment/${id}`)
    },

    generateLinkExcelAppointments() {
      this.setIsLoading(true)
      this.generateAdminAppointmentExcel()
          .then((response) => this.downloadExcel(response))
          .catch((error) => this.showErrorMessage(error.message))
          .finally(() => (this.setIsLoading(false)))
    },

    downloadExcel(response) {
      let blob = new Blob([response.data], {
        type: 'application/vnd.ms-excel;charset=utf-8',
        }),
        url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', this.$t('appointment.adminExcel.fileName') + '.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    setShowExtraHeaders(value) {
      this.showExtraHeaders = value
    },

    setShowExtraHeadersDefaultValue() {
      const userRoles = localStorage.getItem('access_roles')
      if (!userRoles) return

      const roles = userRoles?.split(',')
      this.showExtraHeaders = roles.includes('ROLE_PARTNER')
    },

    refreshAppointments() {
      this.selectedAppointments = []
      this.fetchAppointmentsAsAdmin()
    },

    emitModalAndAppointment(appointment, modal, fetchAppointment, confirmModal = false) {
      if (confirmModal) this.fetchConfirmAppointmentData(appointment?.id)

      if (modal) this.openModal(modal)

      if (fetchAppointment) {
        this.setIsAppointmentLoading(true)
        this.fetchAppointmentAsAdmin(appointment?.id).finally(() => this.setIsAppointmentLoading(false))
      }
    },

    setMenuOptions(appointment) {
      this.menuOptions = this.options(appointment)
    },

    action(appointment, option) {
      if (option.type === 'dispute') {
        this.$store.commit('setIsDisputeDrawerOpen', true)
        this.emitModalAndAppointment(appointment, option.modal, option.fetchAppointment)
        return
      }

      if (typeof option.action === 'function') {
        option.action()
        return
      }

      this.emitModalAndAppointment(appointment, option.modal, option.fetchAppointment)
    },
  },

  watch: {
    appointments() {
      this.hostId && (this.hostName = this.appointments[0]?.host?.name)
      this.selectedAppointments = []
    },
  },

  mounted() {
    this.hostId = this.$router?.currentRoute?.params?.hostId

    this.hostId &&
      this.setFilters({
        hostIds: [this.hostId],
        pendingPaymentsOnly: true,
        status: 'DONE',
      })

    this.setShowExtraHeadersDefaultValue()
  },

  beforeDestroy() {
    this.hostId = ''
    this.hostName = ''
    this.setAppointments([])
    this.selectedAppointments = []
  },
}
</script>
